import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Calendar,
  Clock,
  Send,
  Pause,
  Play,
  Trash2,
  Plus,
  RefreshCw,
  Loader2,
  BarChart3,
  Zap,
  ChevronLeft,
  ChevronRight,
  Mail,
  Users,
  TrendingUp,
  Target,
  Eye,
  MousePointer,
} from 'lucide-react';
import { adminAPI } from '@/services/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-700',
  sending: 'bg-yellow-100 text-yellow-700',
  sent: 'bg-green-100 text-green-700',
  paused: 'bg-orange-100 text-orange-700',
  cancelled: 'bg-red-100 text-red-700',
  draft: 'bg-slate-100 text-slate-700',
};

export default function AdminEmailSchedulingPage() {
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [analytics, setAnalytics] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    preview_text: '',
    html_content: '',
    scheduled_at: '',
    send_optimization: 'none',
    timezone: 'UTC',
  });
  const [saving, setSaving] = useState(false);
  const perPage = 10;

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminAPI.get('/admin/email-scheduling', {
        params: { page, per_page: perPage }
      });
      setEmails(response.data.scheduled_emails || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch scheduled emails:', error);
      toast.error('Failed to load scheduled emails');
    }
    setLoading(false);
  }, [page]);

  const fetchAnalytics = async () => {
    try {
      const response = await adminAPI.get('/admin/email-scheduling/analytics', {
        params: { days: 30 }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  useEffect(() => {
    fetchEmails();
    fetchAnalytics();
  }, [fetchEmails]);

  const handlePause = async (id) => {
    try {
      await adminAPI.post(`/admin/email-scheduling/${id}/pause`);
      toast.success('Email paused');
      fetchEmails();
    } catch (error) {
      toast.error('Failed to pause email');
    }
  };

  const handleResume = async (id) => {
    try {
      await adminAPI.post(`/admin/email-scheduling/${id}/resume`);
      toast.success('Email resumed');
      fetchEmails();
    } catch (error) {
      toast.error('Failed to resume email');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this scheduled email?')) return;
    try {
      await adminAPI.delete(`/admin/email-scheduling/${id}`);
      toast.success('Email deleted');
      fetchEmails();
    } catch (error) {
      toast.error('Failed to delete email');
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.subject || !formData.scheduled_at) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      await adminAPI.post('/admin/email-scheduling', formData);
      toast.success('Email scheduled successfully');
      setCreateModalOpen(false);
      setFormData({
        name: '',
        subject: '',
        preview_text: '',
        html_content: '',
        scheduled_at: '',
        send_optimization: 'none',
        timezone: 'UTC',
      });
      fetchEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule email');
    }
    setSaving(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-6" data-testid="admin-email-scheduling-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">Email Scheduling</h2>
          <p className="text-slate-500">Schedule and optimize email campaigns with AI-powered send time optimization</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchEmails} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setCreateModalOpen(true)} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Schedule Email
          </Button>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Campaigns Sent (30d)</p>
                  <p className="text-3xl font-bold text-navy-700">{analytics.total_campaigns_sent}</p>
                </div>
                <div className="p-3 rounded-full bg-blue-100">
                  <Send className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Emails Sent</p>
                  <p className="text-3xl font-bold text-green-600">{analytics.total_emails_sent.toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-full bg-green-100">
                  <Mail className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avg Open Rate</p>
                  <p className="text-3xl font-bold text-purple-600">{analytics.avg_open_rate}%</p>
                </div>
                <div className="p-3 rounded-full bg-purple-100">
                  <Eye className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avg Click Rate</p>
                  <p className="text-3xl font-bold text-orange-600">{analytics.avg_click_rate}%</p>
                </div>
                <div className="p-3 rounded-full bg-orange-100">
                  <MousePointer className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Optimization Recommendation */}
      {analytics?.optimization_recommendation && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-green-200">
                <Zap className="w-5 h-5 text-green-700" />
              </div>
              <div>
                <h4 className="font-semibold text-green-800">AI Send Time Recommendation</h4>
                <p className="text-sm text-green-700 mt-1">
                  {analytics.optimization_recommendation.reasoning}
                </p>
                <p className="text-xs text-green-600 mt-2">
                  Best time: {analytics.optimization_recommendation.recommended_hour}:00 on day {analytics.optimization_recommendation.recommended_day + 1}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduled Emails Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-navy-500" />
            Scheduled Emails
          </CardTitle>
          <CardDescription>
            View and manage your scheduled email campaigns
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No scheduled emails</p>
              <Button onClick={() => setCreateModalOpen(true)} className="mt-4" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Schedule Your First Email
              </Button>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Scheduled For</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emails.map((email) => (
                    <TableRow key={email.id}>
                      <TableCell className="font-medium">{email.name}</TableCell>
                      <TableCell className="max-w-xs truncate">{email.subject}</TableCell>
                      <TableCell>{formatDate(email.scheduled_at)}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[email.status] || STATUS_COLORS.draft}>
                          {email.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{email.recipient_count || '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {email.status === 'scheduled' && (
                            <Button variant="ghost" size="sm" onClick={() => handlePause(email.id)}>
                              <Pause className="w-4 h-4" />
                            </Button>
                          )}
                          {email.status === 'paused' && (
                            <Button variant="ghost" size="sm" onClick={() => handleResume(email.id)}>
                              <Play className="w-4 h-4" />
                            </Button>
                          )}
                          {email.status !== 'sent' && email.status !== 'sending' && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(email.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Schedule New Email</DialogTitle>
            <DialogDescription>
              Set up a new scheduled email campaign
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Campaign Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Monthly Newsletter"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Subject Line *</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Your subject here..."
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Preview Text</Label>
              <Input
                value={formData.preview_text}
                onChange={(e) => setFormData({ ...formData, preview_text: e.target.value })}
                placeholder="Text shown in inbox preview..."
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Scheduled Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Send Optimization</Label>
                <Select
                  value={formData.send_optimization}
                  onValueChange={(v) => setFormData({ ...formData, send_optimization: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Send at exact time</SelectItem>
                    <SelectItem value="user_timezone">Adjust for user timezone</SelectItem>
                    <SelectItem value="engagement_based">AI-optimized (engagement based)</SelectItem>
                    <SelectItem value="best_time">Global best time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Email Content (HTML)</Label>
              <Textarea
                value={formData.html_content}
                onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                placeholder="<p>Your email content here...</p>"
                rows={6}
                className="mt-1 font-mono text-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Schedule Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
